import React, { useCallback, useMemo, useState } from 'react'
import { IconButton, Tooltip, makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import FlexRow from '../../../../components/molecules/FlexRow'
import Icon from '../../../../components/atoms/Icon'
import { useProvideFeedback } from '../../../../api/denaliAssistant'
import MessageFeedbackModal from './MessageFeedbackModal'

const useStyles = makeStyles(() => ({
  buttonIcon: {
    fill: 'var(--dai-color-icon-button)',

    '&:hover': {
      fill: 'var(--dai-color-icon-button-hover)'
    },

    '.__selected': {
      fill: 'var(--color-ai-500)'
    }
  }
}))

function MessageButtons ({ message, onRetry, isRetrying, parentInfo }) {
  const classes = useStyles()
  const [copyTooltipText, setCopyTooltipText] = useState('Copy message')
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false)
  const [pendingFeedbackType, setPendingFeedbackType] = useState(null)
  const feedbackMutation = useProvideFeedback()

  const selectedFeedback = useMemo(() => {
    return message.feedback?.type || null
  }, [message.feedback?.type])

  const feedbackType = useMemo(() => {
    return pendingFeedbackType || 'thumbs_up'
  }, [pendingFeedbackType])

  const handleCopy = useCallback(() => {
    if (message.message) {
      navigator.clipboard.writeText(message.message)
        .then(() => {
          setCopyTooltipText('Copied!')
          setTimeout(() => {
            setCopyTooltipText('Copy message')
          }, 2000)
        })
        .catch((error) => {
          console.error('Failed to copy message:', error)
          setCopyTooltipText('Failed to copy')
          setTimeout(() => {
            setCopyTooltipText('Copy message')
          }, 2000)
        })
    }
  }, [message.message])

  const handleThumbsUp = useCallback(() => {
    if (message.threadMessageId) {
      setPendingFeedbackType('thumbs_up')
      setFeedbackModalOpen(true)
    }
  }, [message.threadMessageId])

  const handleThumbsDown = useCallback(() => {
    if (message.threadMessageId) {
      setPendingFeedbackType('thumbs_down')
      setFeedbackModalOpen(true)
    }
  }, [message.threadMessageId])

  const handleFeedbackSubmit = useCallback((feedbackText) => {
    if (message.threadMessageId && pendingFeedbackType && parentInfo?.parentId) {
      feedbackMutation.mutate({
        threadMessageId: message.threadMessageId,
        threadId: parentInfo.parentId,
        type: pendingFeedbackType,
        feedbackText: feedbackText || undefined
      })
    }
    setFeedbackModalOpen(false)
    setPendingFeedbackType(null)
  }, [message.threadMessageId, pendingFeedbackType, feedbackMutation, parentInfo?.parentId])

  const handleFeedbackCancel = useCallback(() => {
    setFeedbackModalOpen(false)
    setPendingFeedbackType(null)
  }, [])

  const handleRetry = useCallback(() => {
    if (onRetry) {
      onRetry(message)
    }
  }, [onRetry, message])

  return (
    <>
      <FlexRow className='gap-sm items-center' style={{ fill: 'var(--dai-color-border1)' }}>
        <Tooltip title={copyTooltipText} arrow>
          <IconButton
            size='small'
            onClick={handleCopy}
          >
            <Icon
              name='copy'
              customSize={20}
              additionalClasses={classes.buttonIcon}
            />
          </IconButton>
        </Tooltip>
        <Tooltip title='Thumbs up' arrow>
          <IconButton
            size='small'
            onClick={handleThumbsUp}
          >
            <Icon
              name='thumbUp'
              customSize={20}
              additionalClasses={clsx(classes.buttonIcon, { __selected: selectedFeedback === 'thumbs_up' })}
            />
          </IconButton>
        </Tooltip>
        <Tooltip title='Thumbs down' arrow>
          <IconButton
            size='small'
            onClick={handleThumbsDown}
          >
            <Icon
              name='thumbDown'
              customSize={20}
              additionalClasses={clsx(classes.buttonIcon, { __selected: selectedFeedback === 'thumbs_down' })}
            />
          </IconButton>
        </Tooltip>
        <Tooltip title='Retry' arrow>
          <IconButton
            size='small'
            onClick={handleRetry}
            disabled={isRetrying}
          >
            <Icon
              name='sync'
              customSize={20}
              additionalClasses={classes.buttonIcon}
            />
          </IconButton>
        </Tooltip>
      </FlexRow>
      <MessageFeedbackModal
        open={feedbackModalOpen}
        onClose={handleFeedbackCancel}
        feedbackType={feedbackType}
        onSubmit={handleFeedbackSubmit}
      />
    </>
  )
}

MessageButtons.propTypes = {
  message: PropTypes.object,
  onRetry: PropTypes.func,
  isRetrying: PropTypes.bool,
  parentInfo: PropTypes.object
}

export default MessageButtons
